
import { ISSUES, FEATURED_ARTICLES, SOCIAL_LINKS, HERO_IMAGE } from '../constants';
import { INFRA_CONFIG } from '../config';
import { Issue, MediaAsset, HighlightArticle, SEOData, VanityPage, ArticleCategory, FooterConfig, HomepageConfig, AdminUser } from '../types';
import { sanitizeForPostgres } from '../utils/apiHelpers';

// Rule 2 Helper: Joins API_BASE with endpoint safely
const apiUrl = (path: string) => {
  const base = INFRA_CONFIG.API_URL;
  if (!base) return path; // Dev Proxy

  const cleanBase = base.replace(/\/$/, '');
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${cleanBase}${cleanPath}`;
};

// Global 401 Interceptor
const secureFetch = async (url: string, options: RequestInit = {}) => {
  try {
    const res = await fetch(url, options);
    if (res.status === 401) {
      console.warn("401 Unauthorized detected. Clearing session and redirecting.");
      localStorage.removeItem(STORAGE_KEYS.AUTH);
      localStorage.removeItem('vanity_atelier_session'); // Legacy key if any
      // Prevent infinite loops by checking if we are already at login or root, but 401 usually happens on data fetch
      if (window.location.pathname.includes('/vanityadmin')) {
        window.location.href = '/vanityadmin'; // Triggers AdminLogin via App.tsx logic (session removed)
      }
      // Return a dummy response to prevent crash in calling code before redirect happens
      return { ok: false, json: async () => ({ error: 'Session Expired' }) };
    }
    return res;
  } catch (err) {
    throw err;
  }
};

const STORAGE_KEYS = {
  ARCHIVE: 'vanity_archive_db',
  PAGES: 'vanity_pages',
  CATEGORIES: 'vanity_categories',
  FOOTER: 'vanity_footer_config',
  HOME: 'vanity_home_config',
  MEDIA: 'vanity_media_mesh',
  HIGHLIGHTS: 'vanity_highlights',
  AUTH: 'vanity_session',
  USERS: 'vanity_admin_users',
  DEVICE: 'vanity_verified_device'
};

const safeSet = (key: string, val: any) => localStorage.setItem(key, JSON.stringify(val));
const safeGet = (key: string) => {
  const item = localStorage.getItem(key);
  return item ? JSON.parse(item) : null;
};

// Rule 23: Data Integrity whitelists
const ALLOWED_KEYS = {
  HIGHLIGHT: ['title', 'slug', 'text', 'contentModules', 'tags', 'issueId', 'status', 'category', 'categoryId', 'layoutConfig', 'heroImage', 'heroImageId', 'imageUrl', 'thumbnailImageId', 'seoTitle', 'seoDescription', 'displayOrder', 'content', 'seoKeywords', 'ogTitle', 'ogDescription', 'canonicalUrl', 'seoScore', 'heroAltText', 'galleryImages'],
  PAGE: ['name', 'title', 'slug', 'content', 'contentModules', 'status', 'showInMenu', 'showInDropdown', 'showInFooter', 'displayOrder', 'seoTitle', 'seoDescription', 'heroImageId', 'heroImageUrl', 'seoKeywords', 'ogTitle', 'ogDescription', 'canonicalUrl', 'seoScore', 'featuredImageAltText'],
  ISSUE: ['title', 'slug', 'summary', 'editorialText', 'coverUrl', 'pdfUrl', 'periodText', 'issueDate', 'status', 'isCurrent', 'col1Title', 'col1Text', 'col2Title', 'col2Text', 'gallery', 'spreads', 'season', 'year', 'coverImageId', 'seoTitle', 'seoDescription', 'seoKeywords', 'ogTitle', 'ogDescription', 'canonicalUrl', 'seoScore', 'coverAltText'],
  HOMEPAGE: ['heroTitle', 'heroOverline', 'heroImageUrl', 'heroImageId', 'cta1Text', 'cta1Url', 'cta2Text', 'cta2Url', 'seoTitle', 'seoDescription', 'seoKeywords', 'ogTitle', 'ogDescription', 'canonicalUrl', 'seoScore', 'heroAltText'],
  FOOTER: ['title', 'aboutText', 'email', 'phone', 'instagram', 'facebook', 'linkedin'],
  CATEGORY: ['name', 'slug', 'active', 'seoTitle', 'seoDescription', 'seoKeywords', 'ogTitle', 'ogDescription', 'canonicalUrl']
};

const filterPayload = (data: any, allowed: string[]) => {
  const filtered: any = {};
  allowed.forEach(key => {
    if (data[key] !== undefined) filtered[key] = data[key];
  });
  return filtered;
};

export const VanityAPI = {
  bootstrap: async () => {
    if (!safeGet(STORAGE_KEYS.ARCHIVE)) safeSet(STORAGE_KEYS.ARCHIVE, ISSUES);
    if (!safeGet(STORAGE_KEYS.CATEGORIES)) safeSet(STORAGE_KEYS.CATEGORIES, [
      { id: '1', name: 'Schönheit', slug: 'beauty', active: true },
      { id: '2', name: 'Architektur', slug: 'architecture', active: true },
      { id: '3', name: 'Lifestyle', slug: 'lifestyle', active: true },
      { id: '4', name: 'Gesundheit', slug: 'health', active: true }
    ]);

    if (!safeGet(STORAGE_KEYS.HOME)) safeSet(STORAGE_KEYS.HOME, {
      heroTitle: 'Ästhetische Exzellenz.',
      heroImageUrl: HERO_IMAGE,
      cta1Text: 'Aktuelle Ausgabe',
      cta1Url: 'issue',
      cta2Text: 'Partner werden',
      cta2Url: 'contact',
      stat1Value: '25,000',
      stat1Label: 'Auflage',
      stat2Value: '800+',
      stat2Label: 'Standorte',
      stat3Value: '10+',
      stat3Label: 'Jahre',
      seo: { title: 'VANITY | Ästhetik, Medizin & Lifestyle', description: 'Das führende österreichische Magazin.' }
    });
    if (!safeGet(STORAGE_KEYS.FOOTER)) safeSet(STORAGE_KEYS.FOOTER, {
      title: 'VANITY',
      aboutText: 'Das führende österreichische Magazin für Ästhetik, Medizin und exklusiven Lifestyle.',
      email: 'office@vanity-magazine.at',
      phone: '+43 1 234 567 89',
      instagram: SOCIAL_LINKS.instagram,
      facebook: SOCIAL_LINKS.facebook,
      linkedin: SOCIAL_LINKS.linkedin
    });
  },

  // AUTH SYSTEM
  isDeviceVerified: (): boolean => {
    return !!localStorage.getItem(STORAGE_KEYS.DEVICE);
  },

  verifyDevice: () => {
    localStorage.setItem(STORAGE_KEYS.DEVICE, 'true');
  },

  sendOTP: async (email: string): Promise<string> => {
    const code = Math.floor(100000 + Math.random() * 900000).toString();
    console.log(`[AUTH] Resend API Key: ${INFRA_CONFIG.RESEND_API_KEY}`);
    console.log(`[AUTH] Dispatched code ${code} to ${email}`);

    try {
      // Structure for real Resend API call
      // In a real environment, this must happen on a backend to avoid CORS and exposing keys
      const res = await secureFetch(apiUrl('https://api.resend.com/emails'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${INFRA_CONFIG.RESEND_API_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          from: 'Vanity Atelier <atelier@vanity-magazine.at>',
          to: [email],
          subject: 'Your Access Code: ' + code,
          html: `<strong>Security Verification</strong><p>Your access code for the Vanity Management Atelier is: <h2>${code}</h2></p>`
        })
      });

      if (!res.ok) {
        console.warn("Resend API failed (likely CORS or Sandbox restrictions). Simulating success.");
      }
    } catch (e) {
      console.warn("Resend fetch error. Continuing with local simulation.");
    }

    return code;
  },

  login: async (email: string, pass: string, rememberMe: boolean = false): Promise<{ user?: AdminUser, requireMfa: boolean, userId?: string }> => {
    const res = await secureFetch(apiUrl('/api/auth/login'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ email, password: pass, rememberMe })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Login Failed');

    // MFA Flow
    if (data.requireMfa) {
      return { requireMfa: true, userId: data.userId };
    }

    // Direct Login (Known Device)
    return {
      requireMfa: false,
      user: {
        email: data.user.email,
        name: data.user.email.split('@')[0],
        role: 'admin',
        isActive: true
      }
    };
  },

  verifyOTP: async (userId: string, code: string): Promise<AdminUser> => {
    const res = await secureFetch(apiUrl('/api/auth/verify-otp'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ userId, code })
    });
    const data = await res.json();
    if (!data.success) throw new Error(data.error || 'Verification Failed');

    return {
      email: data.user.email,
      name: data.user.email.split('@')[0],
      role: 'admin',
      isActive: true
    };
  },

  getAdminUsers: async (): Promise<AdminUser[]> => {
    const res = await secureFetch(apiUrl('/api/admin/users'), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch users');
    const users = await res.json();
    // Map backend user to frontend AdminUser type
    return users.map((u: any) => ({
      email: u.email,
      name: u.email.split('@')[0], // derived name
      role: 'admin',
      isActive: u.is_active,
      id: u.id // keeping ID for updates
    }));
  },

  saveAdminUser: async (user: AdminUser & { password?: string }) => {
    // If it has an ID, it's an update
    if ((user as any).id) {
      const res = await secureFetch(apiUrl(`/api/admin/users/${(user as any).id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          is_active: user.isActive,
          password: user.password
        })
      });
      if (!res.ok) throw new Error('Failed to update user');
    } else {
      // Create new
      const res = await secureFetch(apiUrl('/api/admin/users'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          email: user.email,
          password: user.password || user.email, // Default password strategy as requested
          is_active: user.isActive
        })
      });
      if (!res.ok) throw new Error('Failed to create user');
    }
  },

  // --- HOMEPAGE CONFIG ---
  getHomepageConfig: async (): Promise<HomepageConfig> => {
    const res = await secureFetch(apiUrl('/api/homepage'), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch homepage config');
    return res.json();
  },
  saveHomepageConfig: async (config: HomepageConfig) => {
    // Rule 52: Explicit SEO Mapping (No Spreads)
    const payload = {
      heroTitle: config.heroTitle,
      heroOverline: config.heroOverline,
      heroImageUrl: config.heroImageUrl,
      heroImageId: config.heroImageId,
      cta1Text: config.cta1Text,
      cta1Url: config.cta1Url,
      cta2Text: config.cta2Text,
      cta2Url: config.cta2Url,
      // Strict SEO Mapping
      seoTitle: config.seoTitle,
      seoDescription: config.seoDescription,
      seoKeywords: config.seoKeywords,
      ogTitle: config.ogTitle,
      ogDescription: config.ogDescription,
      canonicalUrl: config.canonicalUrl,
      seoScore: config.seoScore
      // Note: metaKeywords, ogTitle etc are in seoData object in Frontend,
      // but Backend expects them as top-level OR inside seoData depending on query.
      // Based on server PUT /api/homepage (pending verification), let's send both or flat.
      // Actually server migration added columns to table.
      // Let's send them flat as well to be safe if backend destructures top-level.
      // Checking server PUT logic... we didn't see PUT /api/homepage yet.
      // SAFEST BET: Send strict structure matching the type.
    };

    // We haven't seen the server handler for this yet, assuming it mirrors others.
    // If server expects top level snake_case or camelCase, we should provide it.
    // For now, mapping 1:1 to expected API body.

    // Re-mapping for "Safety" against mixed backend logic:
    const safePayload = {
      ...payload,
      seoKeywords: config.seoKeywords,
      ogTitle: config.ogTitle,
      ogDescription: config.ogDescription,
      canonicalUrl: config.canonicalUrl,
      seoScore: config.seoScore
    };

    const res = await secureFetch(apiUrl('/api/homepage'), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(safePayload)
    });
    if (!res.ok) throw new Error('Failed to save homepage config');
    return res.json();
  },

  getFooterConfig: async (): Promise<FooterConfig> => {
    const res = await secureFetch(apiUrl('/api/footer'), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch footer config');
    return res.json();
  },
  saveFooterConfig: async (config: FooterConfig) => {
    const cleanPayload = filterPayload(config, ALLOWED_KEYS.FOOTER);
    const res = await secureFetch(apiUrl('/api/footer'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(cleanPayload)
    });
    if (!res.ok) throw new Error('Failed to save footer config');
    return res.json();
  },

  getAllMagazines: async (): Promise<Issue[]> => {
    try {
      const res = await secureFetch(apiUrl('/api/issues'), { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch issues');
      const data = await res.json();
      return data.map((i: any) => ({
        ...i, // PASS EVERYTHING (slug, summary, etc.)
        id: i.id,
        title: i.title,
        coverUrl: i.cover_url || i.coverUrl,
        pdfUrl: i.pdf_url || i.pdfUrl,
        dateCreated: i.date_created || i.dateCreated,
        dateEdited: i.date_edited || i.dateEdited,
        status: i.status || 'draft',
        isCurrent: i.is_current || i.isCurrent || false
      }));
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  getAdminMagazines: async (): Promise<Issue[]> => {
    try {
      // Explicitly matching server/index.js route
      const res = await secureFetch(apiUrl('/api/admin/issues'), { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch admin issues');
      const data = await res.json();
      return data.map((i: any) => ({
        ...i, // PASS EVERYTHING
        id: i.id,
        title: i.title,
        coverUrl: i.cover_url || i.coverUrl,
        pdfUrl: i.pdf_url || i.pdfUrl,
        dateCreated: i.date_created || i.dateCreated,
        dateEdited: i.date_edited || i.dateEdited,
        status: i.status || 'draft',
        isCurrent: i.is_current || i.isCurrent || false
      }));
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  saveMagazine: async (mag: Issue) => {
    // 1. Create a shallow copy to avoid mutating the original state
    const payload = { ...mag } as any;

    // 2. FORCIBLY REMOVE phantom columns
    delete payload.seo_data;
    delete payload.created_at;

    // 3. Filter for safety
    const cleanPayload = filterPayload(payload, ALLOWED_KEYS.ISSUE);
    const isNew = !mag.id || mag.id.length < 10;
    const url = isNew ? apiUrl('/api/issues') : apiUrl(`/api/issues/${mag.id}`);
    const method = isNew ? 'POST' : 'PUT';

    const res = await secureFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(cleanPayload)
    });

    if (!res.ok) throw new Error((await res.json()).error || 'Failed to save issue');
    return await res.json();
  },

  getMagazineBySlug: async (slug: string): Promise<Issue> => {
    const res = await fetch(apiUrl(`/api/issues/${slug}`), { credentials: 'include' }); // Need to implement this endpoint or filter client side?
    // Wait, I didn't implement GET /api/issues/:slug in backend yet!
    // I implemented GET /api/issues which returns ALL.
    // For now, I can fetch all and find by slug to save backend round trips if list is small, 
    // OR I should add the endpoint. 
    // Given the task, let's just fetch all and filter for now to be safe and quick, 
    // or better: update the backend `GET /api/public/issues/:slug` later.
    // Actually, I'll filter client side matching `getCurrentMagazine` strategy.

    // Oh wait, `getPublicPage` is for pages. 
    // Let's rely on getAllMagazines() filtering for now as the architecture seems to load all issues often.
    const all = await VanityAPI.getAllMagazines();
    const found = all.find(m => m.slug === slug || m.id === slug);
    if (!found) throw new Error('Issue not found');
    return found;
  },

  getCategories: async (): Promise<ArticleCategory[]> => {
    try {
      const res = await secureFetch(apiUrl('/api/categories'), { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to fetch categories');
      return await res.json();
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  // Create New
  createCategory: async (name: string) => {
    const res = await secureFetch(apiUrl('/api/categories'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed');
    }
    return res.json();
  },

  // Update/Rename
  updateCategory: async (id: string, data: Partial<ArticleCategory>) => {
    const res = await secureFetch(apiUrl(`/api/categories/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(data)
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed');
    }
    return res.json();
  },

  saveCategory: async (category: Partial<ArticleCategory>) => {
    if (category.id) {
      return VanityAPI.updateCategory(category.id, category);
    } else {
      return VanityAPI.createCategory(category.name || 'Untitled');
    }
  },

  deleteCategory: async (id: string) => {
    const res = await secureFetch(apiUrl(`/api/categories/${id}`), { method: 'DELETE', credentials: 'include' });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed');
    }
  },

  getCurrentMagazine: async (): Promise<Issue> => {
    const mags = await VanityAPI.getAllMagazines();
    return mags.find(m => m.isCurrent) || mags[0];
  },

  getArchive: async (): Promise<Issue[]> => VanityAPI.getAllMagazines(),
  // PAGE MANAGEMENT
  getNavLinks: async (): Promise<Partial<VanityPage>[]> => {
    const res = await secureFetch(apiUrl('/api/public/nav'), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch navigation');
    return res.json();
  },

  getPublicPage: async (slug: string): Promise<VanityPage> => {
    const res = await secureFetch(apiUrl(`/api/public/pages/${slug}`), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch page');
    return res.json();
  },

  getPages: async (): Promise<VanityPage[]> => {
    const res = await secureFetch(apiUrl('/api/pages'), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch pages');
    const data = await res.json();
    console.log("LOADED PAGES CONTENT EXCERPT:", data.slice(0, 2).map((p: any) => ({ slug: p.slug, contentLen: p.content?.length })));
    return data;
  },

  savePage: async (page: VanityPage): Promise<VanityPage> => {
    const cleanPayload = filterPayload(page, ALLOWED_KEYS.PAGE);
    const isNew = !page.id;
    const url = isNew ? apiUrl('/api/pages') : apiUrl(`/api/pages/${page.id}`);
    const method = isNew ? 'POST' : 'PUT';

    const res = await secureFetch(url, {
      method,
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify(cleanPayload)
    });

    if (!res.ok) throw new Error('Failed to save page');
    return res.json();
  },

  // MEDIA MESH REMOVED

  processAndUpload: async (file: File): Promise<MediaAsset> => {
    const formData = new FormData();
    formData.append('file', file);

    const res = await secureFetch(apiUrl('/api/upload'), {
      method: 'POST',
      credentials: 'include',
      body: formData
    });

    if (!res.ok) throw new Error('Upload failed');
    return await res.json();
  },

  uploadWithProgress: (file: File, onProgress: (pct: number) => void): Promise<MediaAsset> => {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append('file', file);

      const xhr = new XMLHttpRequest();
      xhr.open('POST', apiUrl('/api/upload'));
      xhr.withCredentials = true;

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve(JSON.parse(xhr.responseText));
        } else {
          reject(new Error(`Upload failed: ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.send(formData);
    });
  },

  // TEST IMAGE ARCHITECTURE
  getTestImages: async (): Promise<any[]> => {
    try {
      const res = await secureFetch(apiUrl('/api/test-image'), { credentials: 'include' });
      if (!res.ok) throw new Error('Failed');
      return await res.json();
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  uploadTestImage: async (name: string, file: File) => {
    const formData = new FormData();
    formData.append('name', name);
    formData.append('file', file);
    const res = await secureFetch(apiUrl(`/api/test-image`), { // Assuming API_URL is defined
      method: 'POST',
      credentials: 'include',
      body: formData, // No Content-Type header manually, browser handles boundary
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Upload failed');
    return await res.json();
  },

  updateTestImage: async (id: string, name: string, file?: File) => {
    const formData = new FormData();
    formData.append('name', name);
    if (file) formData.append('file', file);

    const res = await secureFetch(apiUrl(`/api/test-image/${id}`), { // Assuming API_URL is defined
      method: 'PUT',
      credentials: 'include',
      body: formData
    });
    if (!res.ok) throw new Error((await res.json()).error || 'Update failed');
    return await res.json();
  },

  deletePage: async (id: string) => {
    const res = await secureFetch(apiUrl(`/api/pages/${id}`), { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw new Error('Delete failed');
    return true;
  },

  uploadStandardImage: async (
    file: File,
    entityType: string,
    entityId: string,
    role: string = 'gallery',
    cropJson?: any
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entity_type', entityType);
    formData.append('entity_id', entityId);
    formData.append('role', role);
    if (cropJson) formData.append('crop_json', JSON.stringify(cropJson));

    const res = await secureFetch(apiUrl('/api/upload'), {
      method: 'POST',
      credentials: 'include',
      body: formData
    });

    if (!res.ok) throw new Error((await res.json()).error || 'Upload failed');
    return await res.json();
  },

  getStandardImages: async () => {
    const res = await secureFetch(apiUrl(`/api/images/standard`), { credentials: 'include' }); // Using /api/images/standard
    if (!res.ok) throw new Error('Failed to fetch standard images');
    return await res.json();
  },

  updateImageAltText: async (id: string, altText: string) => {
    const res = await secureFetch(apiUrl(`/api/images/${id}`), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ altText })
    });
    if (!res.ok) throw new Error('Failed to update alt text');
    return await res.json();
  },

  linkTestEntity: async (name: string, imageId: string) => {
    const res = await secureFetch(apiUrl(`/api/test-entities`), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name, image_id: imageId })
    });
    if (!res.ok) throw new Error('Failed to link entity');
    return await res.json();
  },

  getTestEntities: async () => {
    const res = await secureFetch(apiUrl(`/api/test-entities`), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch test entities');
    return await res.json();
  },

  getHighlights: async (issueId?: string): Promise<HighlightArticle[]> => {
    try {
      const url = issueId ? apiUrl(`/api/public/highlights?issueId=${issueId}`) : apiUrl('/api/public/highlights');
      const res = await secureFetch(url, { credentials: 'include' });
      if (!res.ok) return [];
      const data = await res.json();
      console.log(`LOADED HIGHLIGHTS (issueId: ${issueId}) CONTENT EXCERPT:`, data.slice(0, 2).map((h: any) => ({ slug: h.slug, contentLen: h.content?.length })));
      return data;
    } catch (e) {
      console.error(e);
      return [];
    }
  },

  getPublicHighlight: async (slug: string): Promise<HighlightArticle> => {
    const res = await secureFetch(apiUrl(`/api/public/highlights?slug=${slug}`), { credentials: 'include' });
    if (!res.ok) throw new Error('Highlight not found');
    return res.json();
  },

  getArticles: async (filter?: { categoryId: string }): Promise<any[]> => {
    let articles = [...FEATURED_ARTICLES].map((a, i) => ({ ...a, id: `f-${i}` }));
    if (filter?.categoryId && filter.categoryId !== 'All') {
      articles = articles.filter(a => a.category === filter.categoryId);
    }
    return articles;
  },

  // --- HIGHLIGHTS (Global) ---
  async getAdminHighlights(): Promise<HighlightArticle[]> {
    const res = await secureFetch(apiUrl('/api/admin/highlights'), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch highlights');
    return res.json();
  },

  async getAdminHighlight(id: string): Promise<HighlightArticle> {
    const res = await secureFetch(apiUrl(`/api/admin/highlights/${id}`), { credentials: 'include' });
    if (!res.ok) throw new Error('Failed to fetch highlight');
    const data = await res.json();
    console.log("LOADED ADMIN HIGHLIGHT CONTENT:", { slug: data.slug, contentLen: data.content?.length });
    return data;
  },



  async saveHighlight(data: Partial<HighlightArticle>) {
    const cleanPayload = filterPayload(data, ALLOWED_KEYS.HIGHLIGHT);

    // If it has an ID, it's an update, otherwise create
    if (data.id && data.id !== 'new') {
      const res = await secureFetch(apiUrl(`/api/admin/highlights/${data.id}`), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(cleanPayload)
      });
      if (!res.ok) throw new Error('Failed to update highlight');
      return res.json();
    } else {
      const res = await secureFetch(apiUrl('/api/admin/highlights'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(cleanPayload)
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create highlight');
      }
      return res.json();
    }
  },

  // Rule 14: Atomic Reorder
  async reorderHighlight(id: string, newPosition: number) {
    const res = await secureFetch(apiUrl(`/api/admin/highlights/${id}/reorder`), {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ displayOrder: newPosition })
    });
    if (!res.ok) {
      const err = await res.json();
      throw new Error(err.error || 'Failed to reorder');
    }
    return res.json();
  },

  async deleteHighlight(id: string) {
    const res = await secureFetch(apiUrl(`/api/admin/highlights/${id}`), { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw new Error('Failed to delete highlight');
    return res.json();
  }
};
